import { useCallback, useEffect, useState } from "react";
import { NotificationsAPI } from "../../APIs/notifications.api";
import { useToast } from "../../components/toast";
import { BellIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { ErrorMessage } from "../../components/error-message";
import { CrudTableHeader } from "../../components/crud-table-header";

export function NotificationsPage() {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { error, handleError } = useErrorHandler();
  const [scheduledStatus, setScheduledStatus] = useState<boolean | null>(null);
  const [lastSentReminder, setLastSentReminder] = useState<string | null>(null);

  const loadScheduledStatus = useCallback(async () => {
    try {
      const response = await NotificationsAPI.getScheduledNotificationsStatus();
      if (response.statusCode === 200) {
        setScheduledStatus(response.data?.isRunning ?? null);
      } else {
        handleError(
          response.message ||
            "Error al obtener el estado de notificaciones programadas"
        );
      }
    } catch (error) {
      handleError("Error al obtener el estado de notificaciones programadas");
    }
  }, [handleError]);

  const loadLastSentReminder = useCallback(async () => {
    try {
      const response = await NotificationsAPI.getLastCompetitionStartReminder();
      if (response.statusCode === 200) {
        setLastSentReminder(response.data?.lastSent ?? null);
      } else {
        handleError(
          response.message ||
            "Error al obtener el recordatorio enviado por última vez"
        );
      }
    } catch (error) {
      handleError("Error al obtener el recordatorio enviado por última vez");
    }
  }, [handleError]);

  const handleSendStartReminder = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await NotificationsAPI.sendCompetitionStartReminder();
      if (response.statusCode === 200) {
        addToast({
          message: response.message || "Recordatorio enviado exitosamente",
          type: "success",
          location: "bottom-center",
        });
        loadLastSentReminder();
      } else {
        addToast({
          message: response.message || "Error al enviar el recordatorio",
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al enviar el recordatorio",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setIsLoading(false);
    }
  }, [addToast, loadLastSentReminder]);

  const handleToggleScheduled = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await NotificationsAPI.toggleScheduledNotifications();
      if (response.statusCode === 200) {
        setScheduledStatus(response.data?.isRunning ?? null);
        addToast({
          message: response.message || "Estado de notificaciones actualizado",
          type: "success",
          location: "bottom-center",
        });
      } else {
        addToast({
          message: response.message || "Error al actualizar el estado",
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al actualizar el estado",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadScheduledStatus();
    loadLastSentReminder();
  }, [loadScheduledStatus, loadLastSentReminder]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <CrudTableHeader
          title="Gestión de Notificaciones"
          icon={<BellIcon className="w-6 h-6" />}
        />

        <div className="space-y-4">
          {error && (
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <ErrorMessage message={error} dismissible />
            </div>
          )}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Notificaciones Programadas
            </h2>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    scheduledStatus ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {scheduledStatus === null
                      ? "Cargando estado..."
                      : scheduledStatus
                      ? "Notificaciones activas"
                      : "Notificaciones desactivadas"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {scheduledStatus === null
                      ? "Verificando estado del sistema..."
                      : scheduledStatus
                      ? "El sistema está enviando notificaciones automáticamente para recordar pasos aún no completados a los equipos"
                      : "Las notificaciones automáticas están desactivadas"}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleScheduled}
                disabled={isLoading}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white ${
                  scheduledStatus
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Procesando...
                  </>
                ) : scheduledStatus ? (
                  "Desactivar"
                ) : (
                  "Activar"
                )}
              </button>
            </div>
          </div>

          <div className="bg-green-50 rounded-xl p-4 border border-green-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Recordatorio de Inicio de Competencia
            </h2>
            <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
              <div>
                {lastSentReminder ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Último recordatorio enviado:
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(lastSentReminder).toLocaleString("es-ES", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <p className="text-sm text-gray-600">
                      No se ha enviado ningún recordatorio aún.
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={handleSendStartReminder}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Procesando...
                  </>
                ) : (
                  "Enviar Recordatorio"
                )}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Notificaciones por Equipo
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Gestiona las notificaciones específicas para cada equipo
                </p>
              </div>
              <button
                onClick={() => navigate("/dashboard/notifications/teams")}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <BellIcon className="w-5 h-5 mr-2" />
                Ir a Notificaciones por Equipo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
